// It's Me — the director. Every frame of the video is a pure function of song time: which world, which camera,
// where every word lands. The lyrics are the stage; the worlds (stage lights, the fan feed, a jewel box, paint,
// manga speed lines, the Sphere, a wall of posters, a mirror, a star) are cut to the bar and knocked by the kick.
(function () {
  const H = window.HANA, G = H.G, A = H.Atlas, SONG = window.HANA_SONG;
  const { clamp, lerp, smooth } = H;
  const D = H.Director = {};

  // ---- palette: idol pink, lemon, penlight cyan, stage violet, ink
  const PINK = [1, 0.16, 0.52], HOT = [1, 0.3, 0.72], YEL = [1, 0.9, 0.22], CYAN = [0.22, 0.92, 1], VIO = [0.45, 0.18, 1];
  const RED = [0.92, 0.06, 0.18], WHITE = [1, 0.97, 0.99], INK = [0.045, 0.01, 0.06], TEAL = [0.08, 0.78, 0.74], GOLD = [1, 0.78, 0.35];
  const CORAL = [1, 0.36, 0.33], TEAL0 = [0.03, 0.075, 0.08], BONE = [1, 0.95, 0.9];
  const mixc = (a, b, k) => [lerp(a[0], b[0], k), lerp(a[1], b[1], k), lerp(a[2], b[2], k)];
  const sc = (a, k) => [a[0] * k, a[1] * k, a[2] * k];
  const SERIF = 0, GOTH = 1;

  // a punch: what's left of an entrance k seconds in, over T seconds, log-shaped (most of it gone on the first frame)
  const punch = (k, T) => { if (k <= 0) return 1; if (k >= T) return 0; const tau = T / 14; return 1 - Math.log(1 + k / tau) / Math.log(1 + T / tau); };
  const hash = n => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); };
  const h2 = (a, b) => hash(a * 17.31 + b * 3.77);

  // ---- the beat
  const KICK = SONG.kicks || [], KAMP = SONG.kickAmp || [], SN = SONG.snares || [], BEATS = SONG.beats || [];
  function count(arr, t) { let lo = 0, hi = arr.length; while (lo < hi) { const m = (lo + hi) >> 1; if (arr[m] <= t) lo = m + 1; else hi = m; } return lo; }
  function hit(arr, t, k) { const i = count(arr, t) - 1; if (i < 0) return { i: -1, age: 9, env: 0 }; const age = t - arr[i]; return { i, age, env: Math.exp(-age * k) }; }
  function beatPhase(t) { const i = count(BEATS, t) - 1; if (i < 0 || i >= BEATS.length - 1) return t / 0.395; return i + (t - BEATS[i]) / (BEATS[i + 1] - BEATS[i]); }

  // ---- the lyrics, timed
  const LINES = (H.text('lyrics') || []).map((ln, i, arr) => {
    const words = ln.split && ln.split.join('') === ln.line.replace(/\s/g, '') ? ln.split.slice() : ln.line.split(/\s+/).filter(Boolean);
    let w = Array.isArray(ln.w) && ln.w.length === words.length ? ln.w.slice() : null;
    const next = arr[i + 1];
    if (!w) { const span = Math.max(1, Math.min(3, (next ? next.at : ln.at + 3) - ln.at - 0.3)); w = words.map((_, j) => ln.at + span * j / words.length); }
    const last = w[w.length - 1];
    const end = next ? Math.min(next.at, last + 2.6) : last + 2.6;
    return { i, at: ln.at, line: ln.line, words, w, end, last };
  });
  function lineAt(t) { let c = null; for (const L of LINES) { if (L.at - 0.02 <= t && t < L.end) c = L; } return c; }
  function prevLine(L) { return L && L.i > 0 ? LINES[L.i - 1] : null; }
  const is = (L, s) => !!L && L.line.startsWith(s);
  const isQ = L => /^Who’s/.test(L.line);
  const isA = L => /^I’m your bias/.test(L.line);

  // ---- type layout (em units), cached
  const LAY = new Map();
  function hlay(s, fi) {
    const key = fi + '|h|' + s; let L = LAY.get(key); if (L) return L;
    const chars = []; let x = 0;
    for (const ch of s) {
      if (ch === ' ') { x += 0.26; continue; }
      const g = A.get(fi, ch); if (!g) { x += 0.4; continue; }
      chars.push({ ch, g, x, adv: g.adv }); x += g.adv;
    }
    L = { chars, w: x }; LAY.set(key, L); return L;
  }
  function vlay(s, fi) {
    const key = fi + '|v|' + s; let L = LAY.get(key); if (L) return L;
    const chars = []; let y = 0;
    for (const ch of s) {
      if (ch === ' ') { y += 0.5; continue; }
      const g = A.get(fi, ch); if (!g) { y += 1; continue; }
      chars.push({ ch, g, y, adv: g.adv }); y += 1;
    }
    L = { chars, h: y }; LAY.set(key, L); return L;
  }
  const widthOf = (s, fi) => hlay(s, fi).w;

  // draw a string. o: col, a, line, glow, add, rot, skew, sx, ax (0 left .5 centre 1 right), ay (vertical: 0 top .5 middle),
  // track (em), vert, each(i, n, ch) -> { dx, dy, s, r, a, col, hide }, rough, bold, z
  const ROT = (v, r) => [v[0] * Math.cos(r) - v[1] * Math.sin(r), v[0] * Math.sin(r) + v[1] * Math.cos(r)];
  function text(s, fi, x, y, sz, o) {
    o = o || {};
    const z = o.z || 0, col = o.col || WHITE, a0 = o.a == null ? 1 : o.a;
    const rot = o.rot || 0, sk = o.skew || 0, sx = o.sx || 1, tr = o.track || 0;
    const c = Math.cos(rot), sn = Math.sin(rot);
    const ux = [c * sz * sx, sn * sz * sx];
    const uy = [-sn * sz + sk * ux[0], c * sz + sk * ux[1]];
    const ce = A.cellEm / 2, pe = A.padEm;
    let chars, n;
    if (o.vert) {
      const L = vlay(s, fi); chars = L.chars; n = chars.length;
      const Hv = L.h * (1 + tr);
      const ay = o.ay == null ? 0.5 : o.ay;
      for (let i = 0; i < n; i++) {
        const ch = chars[i];
        const e = o.each ? o.each(i, n, ch.ch) : null;
        if (e && e.hide) continue;
        const cy = -(ch.y + 0.5) * (1 + tr) + ay * Hv;
        let lx = -ch.adv / 2 + ce - pe + (e ? e.dx || 0 : 0), ly = cy - 0.36 + 0.32 + (e ? e.dy || 0 : 0);
        put(ch.g, x, y, z, ux, uy, lx, ly, ce * (e && e.s != null ? e.s : 1), e ? e.r || 0 : 0, e && e.col || col, a0 * (e && e.a != null ? e.a : 1), o);
      }
      return { w: sz, h: Hv * sz };
    }
    const L = hlay(s, fi); chars = L.chars; n = chars.length;
    const Wt = L.w + tr * Math.max(0, n - 1);
    const ax = o.ax == null ? 0.5 : o.ax;
    const base = o.base ? 0 : -0.36;
    for (let i = 0; i < n; i++) {
      const ch = chars[i];
      const e = o.each ? o.each(i, n, ch.ch) : null;
      if (e && e.hide) continue;
      let lx = -ax * Wt + ch.x + tr * i + ce - pe + (e ? e.dx || 0 : 0);
      let ly = base + 0.32 + (e ? e.dy || 0 : 0);
      put(ch.g, x, y, z, ux, uy, lx, ly, ce * (e && e.s != null ? e.s : 1), e ? e.r || 0 : 0, e && e.col || col, a0 * (e && e.a != null ? e.a : 1), o);
    }
    return { w: Wt * sz * sx, h: sz };
  }
  function put(g, x, y, z, ux, uy, lx, ly, half, r, col, a, o) {
    const wx = x + ux[0] * lx + uy[0] * ly, wy = y + ux[1] * lx + uy[1] * ly;
    let hx = [ux[0] * half, ux[1] * half], hy = [uy[0] * half, uy[1] * half];
    if (r) { hx = ROT(hx, r); hy = ROT(hy, r); }
    G.quad(wx, wy, z, hx[0], hx[1], 0, hy[0], hy[1], 0, g, col[0], col[1], col[2], a, 0, o.line || 0, o.glow || 0, o.add || 0, 0, o.rough || 0, o.bold || 0, 0);
  }
  // fit a string to a width
  const fit = (s, fi, w) => w / Math.max(0.3, widthOf(s, fi));

  // shapes: half sizes w, h
  function shape(kind, x, y, w, h, rot, col, a, o) {
    o = o || {};
    const c = Math.cos(rot || 0), s = Math.sin(rot || 0);
    G.quad(x, y, o.z || 0, c * w, s * w, 0, -s * h, c * h, 0, null, col[0], col[1], col[2], a, kind, o.line || 0, o.glow || 0, o.add || 0, o.f0 || 0, o.f1 || 0, o.f2 || 0, o.f3 || 0);
  }
  const rect = (x, y, w, h, rot, col, a, o) => shape(1, x, y, w, h, rot, col, a, o);

  // ---- the frame's state
  let sphereWord = null;
  let AS = 1.78, T = 0, F = 0, K = null, S_ = null, BP = 0;

  // words set in a row with measured widths: { j: placement }
  function row(words, js, fi, size, x, y, ax, extra) {
    const gap = 0.26 * size, ws = js.map(j => widthOf(words[j], fi) * size);
    const total = ws.reduce((a, b) => a + b, 0) + gap * (js.length - 1);
    let cx = x - (ax == null ? 0.5 : ax) * total;
    const out = {};
    js.forEach((j, k) => { out[j] = Object.assign({ x: cx, y, size, fi, ax: 0 }, extra || {}); cx += ws[k] + gap; });
    return out;
  }
  // ---- the logo, as its seven blocks (from the artist's SVG, 126 × 40)
  const LOGO = [[69.68, 0, 83.48, 13.33], [69.68, 26.67, 83.48, 40], [0.65, 26.67, 14.45, 40], [0.65, 0, 14.45, 13.33],
    [23.67, 0, 37.47, 40], [46.68, 0, 60.48, 40], [97.28, 0, 111.08, 40], [83.48, 13.33, 124.87, 26.67]];
  function logo(x, y, w, col, a, o) {
    o = o || {};
    const k = w / 124.2, cx = 62.76, cy = 20;
    LOGO.forEach((b, i) => {
      const e = o.each ? o.each(i) : null;
      if (e && e.hide) return;
      const bx = x + ((b[0] + b[2]) / 2 - cx) * k + (e ? e.dx || 0 : 0), by = y - ((b[1] + b[3]) / 2 - cy) * k + (e ? e.dy || 0 : 0);
      const s = e && e.s != null ? e.s : 1;
      rect(bx, by, (b[2] - b[0]) / 2 * k * s, (b[3] - b[1]) / 2 * k * s, 0, (e && e.col) || col, a * (e && e.a != null ? e.a : 1), { z: o.z || 0, add: o.add || 0, glow: o.glow || 0 });
    });
  }

  // the words drawn on the crisp top layer (after bloom and trails)
  function top(fn) { const prev = G.layer(1); try { fn(); } finally { G.layer(prev); } }
  // ---- reusable treatments
  // a line slammed on, word by word (each word: x, y, size, font, colour…)
  function slam(L, t, place, o) { top(() => slam_(L, t, place, o)); }
  function slam_(L, t, place, o) {
    o = o || {};
    const n = L.words.length;
    for (let j = 0; j < n; j++) {
      const p = place(j, n, L.words[j]);
      if (!p) continue;
      const k = t - L.w[j];
      if (k < -0.02 && !o.pre) continue;
      const pop = k < 0 ? 0 : punch(k, 0.13);
      const s = (p.size || 0.4) * (1 + (o.pop == null ? 0.55 : o.pop) * pop);
      const jit = (S_.env * 0.025 + (o.shake || 0)) * (h2(F, j) - 0.5);
      const each = o.cascade === false ? p.each : (i, nn, ch) => {
        const kc = k - i * (o.cascade || 0);
        if (kc < 0) return { hide: true };
        const e = p.each ? p.each(i, nn, ch) : {};
        return Object.assign({}, e, { dy: (e.dy || 0) + 0.25 * punch(kc, 0.1) * (o.drop == null ? 1 : o.drop) });
      };
      const base = { col: p.col, line: p.line, glow: Math.min(p.glow || 0, 0.12), rot: p.rot, skew: p.skew, sx: p.sx, ax: p.ax, ay: p.ay, vert: p.vert, track: p.track, z: p.z, bold: p.bold, rough: p.rough, a: p.a, each };
      // the colour split as it lands
      if (k >= 0 && k < 0.15 && o.ghost !== false) {
        const d = 0.06 * punch(k, 0.15) * s;
        text(L.words[j], p.fi, p.x + d + jit, p.y, s, Object.assign({}, base, { col: CYAN, add: 1, a: 0.7 }));
        text(L.words[j], p.fi, p.x - d + jit, p.y, s, Object.assign({}, base, { col: HOT, add: 1, a: 0.7 }));
      }
      if (p.shadow) text(L.words[j], p.fi, p.x + jit + p.shadow[0] * s, p.y + p.shadow[1] * s, s, Object.assign({}, base, { col: p.shadowCol || INK, line: 0 }));
      text(L.words[j], p.fi, p.x + jit, p.y, s, base);
    }
  }
  // an echo of the line before: outlined, drifting, fading
  function echo(L, t, place, life) { top(() => echo_(L, t, place, life)); }
  function echo_(L, t, place, life) {
    if (!L) return;
    const age = t - L.end;
    if (age < 0 || age > (life || 0.5)) return;
    const k = age / (life || 0.5);
    for (let j = 0; j < L.words.length; j++) {
      const p = place(j, L.words.length, L.words[j]); if (!p) continue;
      text(L.words[j], p.fi, p.x - k * 0.2, p.y + k * 0.05, (p.size || 0.4) * (1 + k * 0.25), { col: p.echoCol || p.col, line: 0.09, a: (1 - k) * 0.7, rot: p.rot, skew: p.skew, sx: p.sx, ax: p.ax, vert: p.vert, z: p.z });
    }
  }
  // rows of repeating words scrolling across the frame
  function wall(words, t, o) {
    const rows = o.rows || 7, size = o.size || 0.32, gap = o.gap || 0.28, z = o.z || -1.5;
    const spanW = AS * (1 + -z / G.D) * 1.25 + 1;
    for (let r = 0; r < rows; r++) {
      const y = (r - (rows - 1) / 2) * size * (o.lead || 1.15) + (o.y || 0);
      const word = words[(r + (o.shift || 0)) % words.length];
      const fi = o.fi == null ? GOTH : o.fi;
      const ww = widthOf(word, fi) * size + gap;
      const dir = r % 2 ? 1 : -1;
      const off = ((t * (o.speed || 0.5) * dir + r * 0.37) % ww + ww) % ww;
      for (let x = -spanW - ww + off; x < spanW; x += ww) {
        const lit = o.lit && o.lit(r, Math.round((x - off) / ww));
        text(word, fi, x, y, size, { z, ax: 0, col: lit ? o.litCol : o.col, line: lit ? 0 : (o.line == null ? 0.08 : (r % 2 ? o.line : 0)), a: lit ? 1 : o.a, add: o.add, rot: o.rot || 0 });
      }
    }
  }
  // bursts of hearts and stars thrown on every kick
  function bursts(t, o) {
    const i1 = count(KICK, t) - 1;
    for (let i = Math.max(0, i1 - 6); i <= i1; i++) {
      const age = t - KICK[i];
      if (age > (o.life || 1.1) || (KAMP[i] || 0) < (o.min || 0.35)) continue;
      const ox = o.x != null ? o.x : (hash(i * 1.7) - 0.5) * AS * 1.6, oy = o.y != null ? o.y : (hash(i * 2.9) - 0.5) * 1.2;
      const N = o.n || 10;
      for (let q = 0; q < N; q++) {
        const ang = hash(i * 31 + q) * 6.283, sp = 0.6 + hash(i * 7 + q * 3) * 1.6;
        const x = ox + Math.cos(ang) * sp * age, y = oy + Math.sin(ang) * sp * age - 0.9 * age * age;
        const kind = [5, 3, 4][(i + q) % 3];
        const s = (0.03 + 0.05 * hash(q * 13 + i)) * (1 - age / (o.life || 1.1)) * (o.scale || 1);
        const col = o.cols[(i + q) % o.cols.length];
        shape(kind, x, y, s, s, hash(q + i) * 0.6 - 0.3, col, 1, { glow: 0.6 });
      }
    }
  }
  // twinkling sparkles scattered over the frame
  function sparkles(t, n, o) {
    for (let i = 0; i < n; i++) {
      const life = 0.6 + hash(i * 3.1) * 1.2, ph = ((t + hash(i) * 7) / life);
      const k = ph - Math.floor(ph), cyc = Math.floor(ph);
      const x = (h2(i, cyc) - 0.5) * AS * 2.1, y = (h2(i + 50, cyc) - 0.5) * 2.1;
      const s = (o.size || 0.04) * (0.4 + hash(i * 5.3) * 1.2) * Math.sin(k * Math.PI);
      shape(o.kind || 3, x, y, s, s, 0, o.cols[i % o.cols.length], 1, { glow: 0.8, add: o.add == null ? 1 : o.add, z: o.z || 0 });
    }
  }
  // viewfinder corners
  function brackets(w, h, col, a, th) {
    const L = 0.12;
    for (const sx of [-1, 1]) for (const sy of [-1, 1]) {
      rect(sx * (w - L / 2), sy * h, L / 2, th, 0, col, a);
      rect(sx * w, sy * (h - L / 2), th, L / 2, 0, col, a);
    }
  }
  // a marquee band
  function ticker(str, y, size, speed, col, bgc, t, o) {
    o = o || {};
    rect(0, y, AS * 1.3, size * 0.75, o.rot || 0, bgc, o.a == null ? 1 : o.a);
    const ww = widthOf(str, GOTH) * size + 0.3;
    const off = ((t * speed) % ww + ww) % ww;
    for (let x = -AS * 1.3 - ww + off; x < AS * 1.3; x += ww) text(str, GOTH, x, y + Math.sin(o.rot || 0) * x, size, { ax: 0, col, rot: o.rot || 0 });
  }

  // ---- the scenes
  // each: (t, lt) -> { bg, cam, post, trail, bloom }; draws its own type and shapes in order (back to front)
  const SC = {};

  // 0–1.8: a signal, a glint
  SC.pre = function (t) {
    const k = clamp(t / 1.8, 0, 1);
    const g = 0.02 + 0.12 * k * k;
    shape(3, 0, 0, g, g, t * 0.4, WHITE, 1, { glow: 1, add: 1 });
    shape(9, 0, 0, g * 3, g * 3, 0, PINK, 0.4 * k, { add: 1 });
    text(H.text('stage.rec'), GOTH, -AS + 0.2, 0.86, 0.07, { ax: 0, col: WHITE, a: 0.8 });
    if (F % 30 < 18) shape(2, -AS + 0.14, 0.865, 0.022, 0.022, 0, RED, 1);
    return { bg: { mode: t > 1.55 ? 11 : 0, a: INK, b: INK, c: INK }, cam: {}, post: { scan: 0.5, grain: 0.1, vig: 0.6 } };
  };

  // 1.8–6.9: the call and answer, a hard cut on each
  function chantPlace(L) {
    const q = isQ(L), v = L.i % 3;
    return (j, n, w) => {
      if (q) {
        if (v === 0) return [{ x: -AS * 0.62, y: 0.42, size: 0.34, fi: SERIF, col: WHITE, ax: 0 }, { x: -AS * 0.62, y: 0.02, size: 0.34, fi: SERIF, col: WHITE, ax: 0 }, { x: -AS * 0.64, y: -0.52, size: 0.78, fi: SERIF, col: PINK, ax: 0, glow: 0.6 }][j];
        if (v === 1) return [{ x: 0, y: 0.55, size: 0.3, fi: SERIF, col: WHITE }, { x: 0, y: 0.22, size: 0.3, fi: SERIF, col: WHITE }, { x: 0, y: -0.35, size: fit('bias?', SERIF, AS * 1.5), fi: SERIF, col: PINK, glow: 0.6 }][j];
        return [{ x: AS * 0.55, y: 0.5, size: 0.5, fi: SERIF, col: WHITE, vert: true }, { x: AS * 0.25, y: 0.5, size: 0.28, fi: SERIF, col: WHITE, ax: 1 }, { x: -AS * 0.1, y: -0.25, size: 0.95, fi: SERIF, col: HOT, skew: 0.18 }][j];
      }
      if (v === 0 || v === 2) return [{ x: -AS * 0.75, y: 0.5, size: 0.42, fi: GOTH, col: INK, ax: 0 }, { x: -AS * 0.75, y: 0.06, size: 0.42, fi: GOTH, col: INK, ax: 0 }, { x: AS * 0.78, y: -0.32, size: fit('bias!', GOTH, AS * 1.2), fi: GOTH, col: WHITE, ax: 1, shadow: [0.04, -0.04], shadowCol: INK }][j];
      return [{ x: 0, y: 0.55, size: 0.36, fi: GOTH, col: INK, sx: 1, ax: 1.05 }, { x: 0, y: 0.55, size: 0.36, fi: GOTH, col: INK, ax: -0.1 }, { x: 0, y: -0.2, size: fit('bias!', GOTH, AS * 1.75), fi: GOTH, col: YEL, shadow: [0.035, -0.035], shadowCol: INK }][j];
    };
  }
  SC.chant = function (t) {
    const L = lineAt(t) || LINES[0];
    const q = isQ(L);
    if (q) {
      room(t, { spin: 0.2 });
      capsStack(t, { from: 0, n: 6, size: 0.3, lead: 0.3, y: 0.78 });
      hud();
    } else {
      wall(['BIAS'], t, { rows: 6, size: 0.42, z: -2, col: PINK, a: 0.9, line: 0.07, speed: 1.2, fi: GOTH });
      slam(L, t, chantPlace(L), { pop: 0.7 });
    }
    if (!q) bursts(t, { cols: [WHITE, YEL, INK], n: 8, life: 0.9 });
    const zoomOut = t > 6.3 ? smooth(6.3, 6.9, t) : 0;
    return {
      bg: q ? { mode: 0, a: TEAL0, b: INK, c: INK } : { mode: 1, a: PINK, b: HOT, c: YEL, p: [12, 0.08, 0, 0], kick: K.env },
      cam: { z: G.D - zoomOut * 2.2, roll: (L.i % 2 ? 0.05 : -0.04) + zoomOut * 0.4 },
      post: { scan: 0.3, dot: q ? 0 : 0.25, duo: q ? 0.75 : 0, grain: q ? 0.09 : 0.06 }, bloom: { s: q ? 0.6 : 0.25, th: q ? 0.6 : 0.9 }
    };
  };

  // 6.9–14.6: the title assembles on a stage of penlights
  SC.title = function (t, lt) {
    if (t < 10.25) {
      // the logo: one block on every kick, on white
      const k0 = count(KICK, 6.9);
      const nOn = count(KICK, t) - k0;
      const kk = hit(KICK, t, 14);
      const order = [4, 5, 3, 2, 0, 1, 6, 7];
      const lw = AS * 1.1 * (1 + smooth(9.4, 10.25, t) * 0.25);
      for (let r = 0; r < 5; r++) text(H.text('title').toUpperCase() + '  ', GOTH, -AS * 1.2 + ((t * 0.3 * (r % 2 ? 1 : -1)) % 1.2), (r - 2) * 0.42, 0.34, { ax: 0, col: [0.82, 0.82, 0.84], line: 0.06, a: 0.7, z: -1 });
      logo(0, 0, lw, INK, 1, { each: (i) => {
        const rank = order.indexOf(i);
        if (rank >= Math.max(1, Math.min(8, nOn + 1)) && t < 9.4) return { hide: true };
        const fresh = rank === nOn ? kk.env : 0;
        return { s: 1 + fresh * 0.5, dx: (h2(i, F >> 1) - 0.5) * 0.04 * fresh, col: fresh > 0.5 ? CORAL : null };
      } });
      const g = smooth(9.9, 10.25, t);
      return {
        bg: { mode: 5, a: WHITE, b: WHITE, c: WHITE, p: [3, 0, 0, 0] },
        cam: { z: G.D - g * 1.5, roll: g * 0.2 },
        post: { rgb: 0.003 + kk.env * 0.01, block: kk.env * 0.4 + g, slice: g * 0.6, scan: 0.15, grain: 0.07, vig: 0.15 },
        bloom: { s: 0.1, th: 0.98 }
      };
    }
    lt = t - 10.25;
    const title = H.text('title').toUpperCase();
    const land = 0.35, k = clamp(lt / land, 0, 1);
    const sz = fit(title, GOTH, AS * 1.45);
    const shake = smooth(12.6, 14.6, t);
    const out = smooth(14.35, 14.75, t);
    sparkles(t, 30, { cols: [WHITE, PINK, CYAN], size: 0.035 });
    if (lt > 0.4) ticker('★ ' + H.text('stage.on') + ' ★ ' + H.text('stage.live') + ' ', -0.78, 0.075, 0.5, INK, YEL, t, { rot: -0.03 });
    const each = (i, n, ch) => {
      const e = 1 - punch(lt - 0.033 * i, land);
      const fl = e < 0.99 && hash(F + i * 7) < 0.35;
      return {
        dx: (h2(i, 1) - 0.5) * 9 * (1 - e) + (h2(F, i) - 0.5) * 0.15 * shake + out * (i - n / 2) * 1.2,
        dy: (h2(i, 2) - 0.5) * 5 * (1 - e) + (h2(F + 3, i) - 0.5) * 0.1 * shake,
        r: (h2(i, 3) - 0.5) * 6 * (1 - e) + out * (h2(i, 4) - 0.5) * 2,
        s: 1 + 2.5 * (1 - e) + out * 2, a: (fl ? 0.2 : 1) * (1 - out)
      };
    };
    top(() => {
      text(title, GOTH, 0.03, 0.05 - 0.03, sz, { col: PINK, each, a: 0.9 });
      text(title, GOTH, 0, 0.05, sz, { col: WHITE, each });
    });
    if (lt > 1.4) {
      const m = H.text('meaning');
      const nn = Math.floor(clamp((lt - 1.4) / 1.0, 0, 1) * [...m].length);
      text(m, SERIF, 0, -0.42, 0.16, { col: YEL, each: i => ({ hide: i >= nn, a: 1 - out }) });
      if (nn < [...m].length && F % 10 < 6) rect(widthOf(m.slice(0, nn), SERIF) * 0.16 - widthOf(m, SERIF) * 0.08 + 0.02, -0.42, 0.006, 0.08, 0, YEL, 1);
    }
    text(H.text('stage.live'), GOTH, AS - 0.25, 0.86, 0.08, { ax: 1, col: WHITE });
    if (F % 30 < 18) shape(2, AS - 0.17, 0.865, 0.022, 0.022, 0, RED, 1);
    return {
      bg: { mode: 2, a: PINK, b: CYAN, c: VIO, p: [0, 0.6 + 0.4 * smooth(10.2, 12, t), -0.45 + 0.4 * smooth(10.2, 12.5, t), 1], kick: K.env, beat: BP },
      cam: { z: G.D + 0.6 - 0.6 * smooth(10.2, 11.6, t) - shake * 0.25, roll: Math.sin(t * 0.7) * 0.02 + (h2(F, 9) - 0.5) * 0.02 * shake },
      post: { rgb: 0.002 + 0.006 * shake, slice: shake * 0.3 }, bloom: { s: 0.55, th: 0.85 }
    };
  };

  // 14.6–20.9: I'm the one, I'm your idol: centre stage
  SC.idol = function (t, lt) {
    const L = lineAt(t);
    const conv = smooth(14.8, 16, t);
    if (is(L, 'I’m the one')) {
      const W_ = L.words, P_ = Object.assign(row(W_, [0, 1, 2], SERIF, 0.2, -AS * 0.82, 0.66, 0, { col: WHITE }), row(W_, [3, 4], SERIF, 0.3, -AS * 0.82, 0.36, 0, { col: YEL }));
      P_[5] = { x: 0.1, y: -0.25, size: fit('idol', GOTH, AS * 1.25), fi: GOTH, col: PINK, glow: 0.25, shadow: [0.03, -0.03], shadowCol: CYAN };
      slam(L, t, (j) => P_[j]);
    }
    if (is(L, 'つねに')) {
      const idolA = 0.25;
      text('idol', GOTH, 0, -0.22, fit('idol', GOTH, AS * 1.25), { col: PINK, line: 0.08, a: idolA });
      for (let c = 0; c < 4; c++) {
        slam(L, t - c * 0.08, () => ({ x: AS * (0.75 - c * 0.22), y: 0.3, size: c === 0 ? 0.26 : 0.2, fi: SERIF, col: c === 0 ? WHITE : PINK, vert: true, line: c ? 0.08 : 0, a: c ? 0.6 - c * 0.12 : 1 }), { ghost: c === 0, drop: 2 });
      }
      slam(L, t, (j) => j === 1 ? { x: -AS * 0.25, y: 0.1, size: 0.62, fi: SERIF, col: YEL, glow: 0.5 } : null, { ghost: true });
    }
    if (is(L, 'キミはまるで')) {
      slam(L, t, (j) => [
        { x: -AS * 0.82, y: 0.55, size: 0.24, fi: SERIF, col: WHITE, vert: true, ay: 0 },
        { x: 0.12, y: 0.08, size: 0.5, fi: SERIF, col: WHITE, glow: 0.3, skew: 0.2 },
        { x: 0.12, y: -0.4, size: fit('fan', GOTH, AS * 0.9), fi: GOTH, col: CYAN, glow: 0.7, shadow: [0.03, -0.03], shadowCol: PINK }][j], { drop: 1.5 });
      bursts(t, { cols: [PINK, CYAN, YEL, WHITE], y: -0.9, n: 12, life: 1.3 });
    }
    if (!L || L.i < 6) {
      // between lines: a heartbeat of the word
      text('IDOL', GOTH, 0, 0, 0.6 + 0.1 * K.env, { col: PINK, line: 0.08, a: 0.5 });
    }
    sparkles(t, 24, { cols: [WHITE, YEL, PINK], size: 0.03 });
    return {
      bg: { mode: 2, a: mixc(PINK, WHITE, 0.15), b: CYAN, c: VIO, p: [0, 1 + 0.4 * conv, 0, 1], kick: K.env, beat: BP },
      cam: { z: G.D - lt * 0.05, roll: Math.sin(BP * Math.PI * 0.5) * 0.02 },
      post: { rgb: 0.002 + K.env * 0.004 }, bloom: { s: 0.6, th: 0.85 }
    };
  };

  // 20.9–24.3: peeking through a fan account
  SC.feed = function (t, lt) {
    const L = lineAt(t);
    const PW = 0.5, PH = 0.92, tilt = -0.06 + Math.sin(t * 1.3) * 0.01;
    const px = AS * 0.12, py = -0.02;
    const R = (x, y) => [px + x * Math.cos(tilt) - y * Math.sin(tilt), py + x * Math.sin(tilt) + y * Math.cos(tilt)];
    // the phone
    rect(px, py, PW + 0.02, PH + 0.02, tilt, INK, 1, { f0: 0.08 });
    rect(px, py, PW, PH, tilt, [0.1, 0.04, 0.14], 1, { f0: 0.07 });
    // header
    let p = R(-PW + 0.13, PH - 0.16); shape(2, p[0], p[1], 0.07, 0.07, 0, PINK, 1);
    shape(5, p[0], p[1] - 0.005, 0.035, 0.035, 0, WHITE, 1);
    p = R(-PW + 0.24, PH - 0.13); text(H.text('feed.handle'), GOTH, p[0], p[1], 0.07, { ax: 0, rot: tilt, col: WHITE });
    p = R(-PW + 0.24, PH - 0.2); text(H.text('feed.account'), SERIF, p[0], p[1], 0.055, { ax: 0, rot: tilt, col: [0.8, 0.7, 0.85] });
    p = R(PW - 0.13, PH - 0.16); rect(p[0], p[1], 0.1, 0.035, tilt, CYAN, 1, { f0: 0.03 });
    text(H.text('feed.follow'), GOTH, p[0], p[1], 0.045, { rot: tilt, col: INK });
    // posts scrolling past
    const scroll = (lt * 0.9) % 0.7;
    for (let i = -1; i < 3; i++) {
      const y = PH - 0.6 - i * 0.7 + scroll;
      if (y > PH - 0.25 || y < -PH + 0.2) continue;
      const pi = i - Math.floor(lt * 0.9 / 0.7);
      p = R(0, y); rect(p[0], p[1], PW - 0.05, 0.27, tilt, [0.2, 0.06, 0.25], 1, { f0: 0.02 });
      shape(8, p[0], p[1], PW - 0.05, 0.27, tilt, [PINK, CYAN, YEL][((pi % 3) + 3) % 3], 0.9, { f0: 0.03, f1: 0.25, f3: 0.25 });
      text(['推し', 'BIAS', '♥', 'ME'][((pi % 4) + 4) % 4], GOTH, p[0], p[1], 0.22, { rot: tilt, col: WHITE });
      p = R(-PW + 0.08, y - 0.31); text(H.text('feed.likes'), GOTH, p[0], p[1], 0.05, { ax: 0, rot: tilt, col: PINK });
    }
    // viewfinder (stolen looks)
    brackets(AS * 0.92, 0.88, WHITE, 0.8, 0.006);
    shape(2, 0, 0, 0.03, 0.03, 0, RED, 0.8, { line: 0.004 });
    text(H.text('stage.rec'), GOTH, -AS * 0.92 + 0.08, 0.8, 0.06, { ax: 0, col: WHITE });
    if (F % 30 < 18) shape(2, -AS * 0.92 + 0.04, 0.8, 0.016, 0.016, 0, RED, 1);
    if (is(L, '盗み見')) {
      slam(L, t, (j) => [
        { x: -AS * 0.62, y: 0.42, size: 0.5, fi: SERIF, col: WHITE, vert: true, glow: 0.4 },
        ...[1, 2, 3].map(j => row(L.words, [1, 2, 3], GOTH, 0.075, px, py + PH - 0.33, 0.5, { col: CYAN })[j])][j], { drop: 0.4 });
      const s = smooth(21.0, 21.3, t);
      rect(-AS * 0.62, 0.42 - 0.75 * s, 0.006, 0.75 * s, 0, RED, 1);
    }
    if (is(L, '僕は')) {
      slam(L, t, (j) => [
        { x: px, y: py - 0.1, size: 0.13, fi: SERIF, col: WHITE, rot: tilt },
        { x: -AS * 0.1, y: -0.35, size: fit('正直さ', SERIF, AS * 1.3), fi: SERIF, col: RED, glow: 0.4, shadow: [0.02, -0.02], shadowCol: WHITE }][j]);
    }
    if (is(L, '止めないで')) slam(L, t, (j) => j === 0 ? { x: -AS * 0.62, y: 0.3, size: 0.3, fi: SERIF, col: WHITE, vert: true } : null);
    const jx = (h2(F >> 1, 1) - 0.5) * 0.02, jy = (h2(F >> 1, 2) - 0.5) * 0.02;
    return { bg: { mode: 12, a: PINK, b: VIO, c: VIO }, cam: { x: jx, y: jy, lx: jx, ly: jy, z: G.D - lt * 0.08 }, post: { scan: 0.5 }, bloom: { s: 0.6 } };
  };

  // 24.3–27.5: 止めないでよ 宝石箱 — a jewel box of light
  SC.jewel = function (t, lt) {
    const L = lineAt(t);
    for (let i = 0; i < 9; i++) {
      const a = i / 9 * 6.283 + t * 0.4, r = 0.75 + 0.1 * Math.sin(t + i);
      const x = Math.cos(a) * r * AS * 0.7, y = Math.sin(a) * r * 0.75;
      const s = 0.08 + 0.05 * hash(i);
      shape(1, x, y, s, s, t * (0.5 + hash(i)) + i, WHITE, 0.9, { line: 0.006, glow: 0.6, add: 1 });
    }
    sparkles(t, 60, { cols: [WHITE, GOLD, HOT, CYAN], size: 0.05 });
    if (is(L, '止めないで')) {
      slam(L, t, (j) => [
        { x: -AS * 0.7, y: 0.25, size: 0.26, fi: SERIF, col: WHITE, vert: true, glow: 0.6 },
        { x: 0.05, y: -0.02, size: fit('宝石箱', SERIF, AS * 1.25), fi: SERIF, col: WHITE, glow: 0.15, shadow: [0.03, -0.03], shadowCol: [0.35, 0.02, 0.25] }][j], { drop: 0.5 });
      if (t > L.w[1]) text('宝石箱', SERIF, 0.05, -0.02, fit('宝石箱', SERIF, AS * 1.25) * (1 + (t - L.w[1]) * 0.3), { col: GOLD, line: 0.06, a: Math.max(0, 0.8 - (t - L.w[1]) * 0.6), add: 1 });
    }
    return { bg: { mode: 4, a: [0.55, 0.12, 0.45], b: [0.15, 0.25, 0.6], c: WHITE, amt: 0.8 }, cam: { z: G.D - lt * 0.15, roll: Math.sin(t * 0.8) * 0.04 }, bloom: { s: 0.6, th: 0.85 }, post: { rgb: 0.002 } };
  };

  // 27.5–40.5: Prada… よその騒ぎ… なぜまたそっち — paint smears in the dark, building to the chorus
  SC.liquid = function (t, lt) {
    const L = lineAt(t);
    const build = smooth(37.4, 40.5, t);
    if (t > 30.9 && t < 33.9) {
      for (let c = 0; c < 7; c++) {
        const x = (c - 3) * 0.42 * AS * 0.5, sp = 0.15 + hash(c) * 0.2;
        const y = ((t * sp + hash(c * 3)) % 1) * 3 - 1.5;
        text('無二無二無二', SERIF, x, y, 0.2, { vert: true, col: c % 2 ? PINK : WHITE, line: c % 3 ? 0.07 : 0, a: 0.55 });
      }
    }
    if (is(L, 'Prada')) {
      slam(L, t, (j) => [
        { x: -AS * 0.82, y: 0.38, size: 0.62, fi: SERIF, col: WHITE, ax: 0 },
        { x: -AS * 0.1, y: -0.12, size: 0.42, fi: SERIF, col: PINK, ax: 0.5, glow: 0.4 },
        { x: AS * 0.15, y: -0.55, size: 0.3, fi: SERIF, col: WHITE, ax: 0.2 }][j], { drop: 0.3 });
      const s = smooth(28.7, 28.85, t);
      if (s > 0) rect(-AS * 0.82 + AS * 0.55 * s, 0.38, AS * 0.55 * s, 0.012, -0.08, WHITE, 1);
      const s2 = smooth(29.9, 30.05, t);
      if (s2 > 0) rect(AS * 0.15, -0.6, AS * 0.32 * s2, 0.01, 0.05, CYAN, 1);
    }
    if (is(L, 'よその')) {
      for (let c = 0; c < 9; c++) {
        const on = hash(c * 7 + (F >> 1)) < 0.6;
        if (!on && c) continue;
        const x = c ? (h2(c, F >> 2) - 0.5) * AS * 1.6 : 0, y = c ? (h2(c + 9, F >> 2) - 0.5) * 1.5 : 0;
        slam(L, t, (j) => ({ x: x + (j ? 0.25 : -0.3) * (c ? 0.5 : 1), y, size: c ? 0.18 : 0.46, fi: SERIF, col: c ? (c % 2 ? CYAN : PINK) : WHITE, line: c ? 0.08 : 0, ax: j ? 0 : 1, rot: c ? (hash(c) - 0.5) * 0.5 : 0 }), { ghost: !c });
      }
    }
    if (is(L, 'なぜ')) {
      const pan = smooth(36.6, 37.1, t);
      slam(L, t, (j) => [
        { x: -AS * 0.6 - pan * 1.5, y: 0.2, size: 0.8, fi: SERIF, col: WHITE, vert: true },
        { x: -AS * 0.25 - pan * 1.5, y: 0.0, size: 0.3, fi: SERIF, col: PINK, vert: true },
        { x: AS * 0.25 + (1 - pan) * 0.0, y: -0.1, size: 0.55, fi: SERIF, col: CYAN, glow: 0.6 }][j]);
      if (t > 36.6) for (let a = 0; a < 3; a++) { const x = AS * 0.25 + 0.5 + a * 0.22 + ((t * 2) % 0.22); shape(7, x, -0.1, 0.06, 0.06, -Math.PI / 2, CYAN, 1, { glow: 0.5 }); }
    }
    if (build > 0) {
      wall(['BIAS', '推し'], t, { rows: 9, size: 0.24, z: -1, col: PINK, a: build * 0.8, speed: 1 + build * 6, line: 0.07 });
      const bf = BP % 1;
      const strobe = build > 0.5 && bf < 0.12 ? 1 : 0;
      text('?', SERIF, 0, 0, 0.6 + build * 2, { col: WHITE, a: build * (0.3 + 0.7 * strobe), glow: 0.5 });
    }
    return {
      bg: { mode: 3, a: PINK, b: TEAL, c: RED, p: [0.6 + 0.4 * K.env, 0, 0, 0] },
      cam: { z: G.D - build * 1.4, x: smooth(36.6, 37.0, t) * 0.0, roll: build * Math.sin(t * 9) * 0.03 },
      trail: { decay: 0.82 - build * 0.2, flow: 0.012, zoom: build * 0.02 },
      post: { rgb: 0.003 + build * 0.012, slice: build * 0.35, flash: t > 40.42 ? smooth(40.42, 40.5, t) : 0 },
      bloom: { s: 0.8 }
    };
  };

  // the chorus: a different composition every bar
  function chorusPlace(L, v) {
    const q = isQ(L);
    const big = q ? 'bias?' : 'bias!';
    const sfi = q ? SERIF : GOTH;
    const lay = [
      // 0: stacked left, last word huge
      () => (j) => [{ x: -AS * 0.85, y: 0.6, size: 0.26, fi: sfi, col: WHITE, ax: 0 }, { x: -AS * 0.85, y: 0.32, size: 0.26, fi: sfi, col: WHITE, ax: 0 }, { x: -AS * 0.88, y: -0.3, size: fit(big, sfi, AS * 1.6), fi: sfi, col: q ? YEL : WHITE, ax: 0, shadow: [0.03, -0.03], shadowCol: INK }][j],
      // 1: centred, giant word across
      () => { const P_ = row(L.words, [0, 1], sfi, 0.3, 0, 0.58, 0.5, { col: INK }); P_[2] = { x: 0, y: -0.15, size: fit(big, sfi, AS * 1.85), fi: sfi, col: WHITE, shadow: [0.035, -0.035], shadowCol: INK }; return (j) => P_[j]; },
      // 2: on a slant
      () => (j) => [{ x: -AS * 0.62, y: 0.42, size: 0.4, fi: sfi, col: YEL, rot: 0.18 }, { x: -AS * 0.62 + widthOf(L.words[0], sfi) * 0.4 * 0.5 + 0.12 + widthOf(L.words[1], sfi) * 0.15, y: 0.42 + Math.sin(0.18) * (widthOf(L.words[0], sfi) * 0.2 + 0.12 + widthOf(L.words[1], sfi) * 0.15), size: 0.3, fi: sfi, col: WHITE, rot: 0.18 }, { x: AS * 0.15, y: -0.25, size: fit(big, sfi, AS * 1.3), fi: sfi, col: WHITE, rot: 0.18, skew: 0.2, shadow: [0.03, -0.03], shadowCol: PINK }][j],
      // 3: down the right side, standing up
      () => (j) => [{ x: -AS * 0.45, y: 0.4, size: 0.5, fi: sfi, col: WHITE }, { x: -AS * 0.45, y: -0.05, size: 0.5, fi: sfi, col: INK }, { x: AS * 0.55, y: 0, size: 0.75, fi: sfi, col: YEL, rot: Math.PI / 2, shadow: [0.03, -0.03], shadowCol: INK }][j],
      // 4: corner and corner
      () => { const P_ = row(L.words, [0, 1], sfi, 0.22, -AS * 0.9, 0.74, 0, { col: WHITE }); P_[2] = { x: AS * 0.92, y: -0.35, size: fit(big, sfi, AS * 1.5), fi: sfi, col: WHITE, ax: 1, shadow: [0.03, -0.03], shadowCol: INK }; return (j) => P_[j]; },
      // 5: words at three depths
      () => (j) => [{ x: -AS * 0.5, y: 0.45, size: 0.5, fi: sfi, col: WHITE, z: -1.5 }, { x: AS * 0.4, y: 0.3, size: 0.35, fi: sfi, col: YEL, z: -0.6 }, { x: 0, y: -0.3, size: fit(big, sfi, AS * 1.3), fi: sfi, col: WHITE, z: 0.6, shadow: [0.03, -0.03], shadowCol: INK }][j]
    ];
    return lay[v % lay.length]();
  }
  function chorusLines(t, o) {
    const L = lineAt(t);
    if (!L) return null;
    if (/^Who\? Who\?/.test(L.line)) {
      slam(L, t, (j) => ({ x: (j ? 1 : -1) * AS * 0.5, y: 0.1 * (j ? -1 : 1), size: fit('Who?', SERIF, AS * 0.85), fi: SERIF, col: j ? CYAN : PINK, glow: 0.1 }));
      return L;
    }
    if (/It’s Me/.test(L.line) || /^Me/.test(L.line)) return L;
    const v = (L.i * 5 + (o.shift || 0)) % 6;
    const prev = prevLine(L);
    if (prev && (isQ(prev) || isA(prev))) echo(prev, t, chorusPlace(prev, (prev.i * 5 + (o.shift || 0)) % 6), 0.6);
    slam(L, t, chorusPlace(L, v), { pop: 0.6 });
    return L;
  }
  // 40.5–52.3, 91.6–103.5, 130–157: the choruses
  SC.pop = function (t, lt, o) {
    o = o || {};
    const L = lineAt(t);
    const who = L && /^Who\? Who\?/.test(L.line);
    const mode = who ? 2 : o.mode;
    if (mode === 1) wall(['BIAS', '推し', 'BIAS', '★'], t, { rows: 8, size: 0.3, z: -1.6, col: WHITE, a: 0.35, speed: 0.9, line: 0.07, lit: (r, c) => h2(r * 13 + c, count(KICK, t)) < 0.08, litCol: YEL });
    if (mode === 9) {
      for (let i = 0; i < 6; i++) { const x = (i - 2.5) * 0.7; text('BIAS', GOTH, x, -0.55, 0.5, { z: -3 - (i % 2), col: i % 2 ? PINK : YEL, line: 0.08, a: 0.7 }); }
    }
    if (mode === 2 && o.screens) {
      for (const sx of [-1, 1]) {
        rect(sx * AS * 1.65, 0.25, 0.55, 0.42, 0, INK, 1, { z: -2.5 });
        for (let r = 0; r < 4; r++) text('BIAS', GOTH, sx * AS * 1.65, 0.55 - r * 0.2, 0.17, { z: -2.5, col: (r + count(KICK, t)) % 2 ? PINK : CYAN, a: 0.9 });
      }
      for (let i = 0; i < 6; i++) {
        const a = Math.sin(t * 2 + i) * 0.7 + (i % 2 ? 0.6 : -0.6);
        shape(6, (i - 2.5) * 0.5, 0.2, 0.012, 1.6, a, i % 2 ? CYAN : PINK, 0.8, { add: 1, z: -1 });
      }
    }
    chorusLines(t, o);
    bursts(t, { cols: mode === 1 ? [WHITE, YEL, INK] : [PINK, YEL, CYAN, WHITE], n: 10 });
    if (mode !== 1) sparkles(t, 20, { cols: [WHITE, PINK, CYAN], size: 0.035 });
    const bgs = {
      1: { mode: 1, a: o.pa || PINK, b: HOT, c: o.pc || YEL, p: [14, 0.1, 0, 0], kick: K.env },
      2: { mode: 2, a: PINK, b: CYAN, c: VIO, p: [0, who ? 1.6 : 1.2, 0, 1], kick: K.env, beat: BP },
      9: { mode: 9, a: PINK, b: INK, c: VIO, p: [3, 0, 0, 0] }
    };
    const vv = L ? L.i : 0;
    return {
      bg: bgs[mode] || bgs[1],
      cam: { z: G.D - 0.12 * Math.sin(lt * 0.9), roll: (vv % 3 - 1) * 0.04 + Math.sin(BP * Math.PI / 2) * 0.01 },
      post: { rgb: 0.002 + K.env * 0.006, dot: mode === 1 ? 0.2 : 0, flash: mode === 2 && o.screens ? K.env * 0.08 : 0 },
      bloom: { s: mode === 1 ? 0.25 : 0.55, th: mode === 1 ? 0.9 : 0.85 }
    };
  };

  // It's Me: a white void, a rainbow halo, one word
  SC.white = function (t, lt) {
    const L = lineAt(t) || LINES.filter(x => x.at <= t && /It’s Me/.test(x.line)).pop();
    const R = 0.62 + 0.03 * K.env;
    sparkles(t, 36, { cols: [HOT, CYAN, GOLD], size: 0.04, add: 0 });
    if (L) {
      slam(L, t, (j) => [
        { x: 0, y: 0.52, size: 0.22, fi: SERIF, col: INK, track: 0.2 },
        { x: 0, y: -0.06, size: 1.15, fi: SERIF, col: INK }][j], { pop: 0.8, ghost: true });
    }
    const flash = Math.max(0, 1 - lt / 0.25);
    return {
      bg: { mode: 5, a: PINK, b: WHITE, c: WHITE, p: [R, 0.2, 0, 0], kick: K.env },
      cam: { z: G.D - lt * 0.12, roll: 0 }, post: { flash: flash * 0.8, scan: 0.1, grain: 0.04, vig: 0.15 }, bloom: { s: 0.15, th: 0.97 }
    };
  };

  // the mirror: ME inside ME inside ME
  SC.mirror = function (t, lt, o) {
    o = o || {};
    const L = lineAt(t) || LINES.filter(x => x.at <= t).pop();
    const inv = o.inv || (L && L.i % 2);
    const zp = (lt * (o.speed || 0.6)) % 1;
    const sz0 = 0.95;
    const N = 9;
    for (let i = N - 1; i >= 0; i--) {
      const s = sz0 * Math.pow(0.55, i - zp);
      if (s > 6) continue;
      const col = i % 2 ? (inv ? INK : YEL) : (inv ? PINK : WHITE);
      text('ME', SERIF, 0, -s * 0.02, s, { col, a: clamp(s * 4, 0, 1), line: i % 3 === 2 ? 0.07 : 0 });
    }
    if (L && /^Me, Me!/.test(L.line)) {
      const k = t - L.w[0];
      const g = 1 + Math.min(4, Math.floor(k / 0.26));
      const nX = Math.pow(2, g), nY = Math.max(1, Math.pow(2, g - 1));
      for (let a = 0; a < nX; a++) for (let b = 0; b < nY; b++) {
        const x = ((a + 0.5) / nX - 0.5) * AS * 2, y = ((b + 0.5) / nY - 0.5) * 2;
        top(() => text('ME', GOTH, x, y, 1.3 / nY, { col: (a + b) % 2 ? PINK : WHITE, glow: 0.15, z: 0.02 }));
      }
    } else if (L && /^Me$/.test(L.line)) {
      top(() => text('ME', GOTH, 0, 0, 1.6 + (t - L.at) * 0.4, { col: PINK, glow: 0.2, z: 0.02 }));
    } else if (L && /It’s Me/.test(L.line)) {
      slam(L, t, (j) => [{ x: 0, y: 0.72, size: 0.2, fi: SERIF, col: inv ? WHITE : INK, track: 0.3, z: 0.05 }, null][j]);
    }
    if (o.shards) {
      for (let i = 0; i < 40; i++) {
        const age = ((t * 0.7 + hash(i)) % 1);
        const a = hash(i * 3) * 6.283, r = age * (1.5 + hash(i * 5) * 2);
        shape(7, Math.cos(a) * r * AS, Math.sin(a) * r, 0.05 + hash(i) * 0.1, 0.05 + hash(i * 2) * 0.12, a + age * 6, i % 2 ? CYAN : WHITE, 1 - age, { line: i % 3 ? 0.006 : 0, glow: 0.5 });
      }
    }
    return {
      bg: { mode: 10, a: inv ? INK : PINK, b: inv ? [0.15, 0.03, 0.2] : HOT, c: inv ? CYAN : YEL, p: [0.55, -(o.speed || 0.6), 0, 0] },
      cam: { z: G.D, roll: (o.spin || 0) * lt + (L ? (L.i % 2 ? 0.06 : -0.06) : 0) },
      trail: { decay: 0.6, zoom: 0.03, flow: 0 },
      post: { rgb: 0.003 + K.env * 0.01, block: K.env * 0.3, inv: 0 }, bloom: { s: 0.5 }
    };
  };

  // Hey, just say I'm your fave!
  SC.fave = function (t, lt) {
    const L = lineAt(t);
    const hs = 0.75 + 0.08 * K.env;
    shape(5, 0, 0.0, hs * 1.15, hs, 0, WHITE, 1, { glow: 0.4 });
    shape(5, 0, 0.0, hs * 1.1, hs * 0.95, 0, PINK, 1);
    for (let i = 0; i < 26; i++) {
      const y = ((t * (0.3 + hash(i) * 0.4) + hash(i * 3)) % 1) * 2.6 - 1.3;
      shape(5, (hash(i * 7) - 0.5) * AS * 2, y, 0.04 + hash(i * 9) * 0.05, 0.04 + hash(i * 9) * 0.05, Math.sin(t * 2 + i) * 0.3, i % 3 ? WHITE : INK, 0.9);
    }
    if (is(L, 'Hey')) {
      slam(L, t, (j) => [
        { x: -AS * 0.62, y: 0.6, size: 0.36, fi: GOTH, col: INK, rot: -0.12 },
        ...[1, 2].map(j => row(L.words, [1, 2], SERIF, 0.17, 0, 0.32, 0.5, { col: WHITE })[j]),
        ...[3, 4].map(j => row(L.words, [3, 4], SERIF, 0.22, 0, 0.08, 0.5, { col: WHITE })[j]),
        { x: 0, y: -0.22, size: fit('fave!', GOTH, 1.1), fi: GOTH, col: YEL, shadow: [0.03, -0.03], shadowCol: INK }][j]);
      const likes = Math.floor(99999 * smooth(63.2, 65.3, t));
      text('♥ ' + likes.toLocaleString('en-US'), GOTH, 0, -0.78, 0.09, { col: INK });
      if (t > 65.2) {
        const k = t - 65.2, s = 1 + 1.5 * punch(k, 0.12);
        rect(AS * 0.55, 0.55, 0.32 * s, 0.11 * s, 0.25, RED, 1, { line: 0.012 });
        text(H.text('fave'), GOTH, AS * 0.55, 0.55, 0.16 * s, { col: RED, rot: 0.25 });
      }
    }
    return { bg: { mode: 1, a: YEL, b: [1, 0.8, 0.1], c: HOT, p: [16, -0.12, 0, 0], kick: K.env }, cam: { z: G.D - lt * 0.1, roll: Math.sin(BP * Math.PI) * 0.03 }, post: { dot: 0.15, rgb: K.env * 0.004 }, bloom: { s: 0.2, th: 0.95 } };
  };

  // 66–75.7: manga speed lines
  SC.speed = function (t, lt) {
    const L = lineAt(t);
    let dark = false;
    if (is(L, 'I yelled')) {
      slam(L, t, (j) => [
        ...[0, 1].map(j => row(L.words, [0, 1], SERIF, 0.2, -AS * 0.8, 0.62, 0, { col: INK })[j]),
        { x: -0.1, y: 0.1, size: 0.95, fi: GOTH, col: INK, shadow: [0.04, -0.04], shadowCol: PINK, rot: -0.06 },
        { x: AS * 0.25, y: -0.55, size: 0.3, fi: GOTH, col: PINK, each: (i) => ({ dy: Math.sin(t * 16 + i * 0.9) * 0.15 }) }][j], { shake: 0.04 });
    }
    if (is(L, 'Oh my')) {
      for (let j = 0; j < 5; j++) {
        const k = t - L.w[j]; if (k < 0) continue;
        const p = j === 0 ? { x: -AS * 0.6, y: 0.4, s: 0.5 } : { x: -AS * 0.3 + j * 0.28 * AS * 0.5, y: 0.25 - j * 0.16, s: 0.35 + j * 0.15 };
        top(() => text(L.words[j], j ? GOTH : SERIF, p.x, p.y, p.s * (1 + 0.5 * punch(k, 0.12)), { col: j % 2 ? PINK : INK, rot: (hash(j) - 0.5) * 0.5 }));
      }
    }
    if (is(L, '袖手観')) {
      slam(L, t, (j) => j === 0 ? { x: -AS * 0.72, y: 0.15, size: 0.5, fi: SERIF, col: INK, vert: true } : null);
      // like tortilla: the words rolled round a turning barrel
      const k = t - L.w[1];
      if (k > -0.05) {
        const roll = t * 2.4, R = 0.42;
        const str = 'like tortilla · like tortilla · ';
        const lay = hlay(str, GOTH), sz = 0.16;
        for (let ring = 0; ring < 5; ring++) {
          for (const ch of lay.chars) {
            const ang = roll * (ring % 2 ? -1 : 1) + (ch.x * sz) / R;
            const c = Math.cos(ang), s = Math.sin(ang);
            if (c < -0.1) continue;
            const x = AS * 0.3 + s * R, z = c * R - R, y = (ring - 2) * 0.24;
            const hx = sz * A.cellEm / 2;
            const col = ring % 2 ? PINK : INK;
            G.quad(x, y, z, hx * c, 0, -hx * s, 0, hx, 0, ch.g, col[0], col[1], col[2], clamp(c + 0.4, 0, 1) * clamp(k * 6, 0, 1), 0, 0, 0, 0, 0, 0, 0, 0);
          }
        }
      }
    }
    if (is(L, 'Why')) {
      dark = L.w.filter(x => x <= t).length % 2 === 0;
      for (let j = 0; j < 4; j++) {
        const k = t - L.w[j]; if (k < 0) continue;
        const cur = j === L.w.filter(x => x <= t).length - 1;
        top(() => text('Why?', SERIF, [-0.5, 0.45, -0.2, 0.3][j] * AS, [0.35, 0.1, -0.35, 0.55][j], (cur ? 0.75 : 0.45) * (1 + 0.5 * punch(k, 0.12)), { col: dark ? WHITE : INK, line: cur ? 0 : 0.08, rot: [-0.2, 0.15, -0.05, 0.3][j], a: cur ? 1 : 0.6 }));
      }
    }
    if (is(L, 'This')) {
      shape(5, 0, 0.0, 0.85, 0.78, 0, RED, 1, { line: 0.012 });
      slam(L, t, (j) => [
        { x: -AS * 0.7, y: 0.65, size: 0.2, fi: SERIF, col: INK },
        { x: 0, y: 0.02, size: 0.85, fi: SERIF, col: RED, glow: 0.2 },
        ...[2, 3, 4].map(j => row(L.words, [2, 3, 4], GOTH, 0.17, 0, -0.72, 0.5, { col: INK })[j])][j]);
    }
    let block = 0, inv = 0;
    if (is(L, 'It can')) {
      dark = true;
      const nOn = L.w.filter(x => x <= t).length;
      for (let j = 1; j < nOn; j++) {
        const k = t - L.w[j];
        const cur = j === nOn - 1;
        for (let r = 0; r < (cur ? 1 : 3); r++) top(() => text('can’t', GOTH, (j - 2.5) * 0.22 + (r - 1) * 0.06 + (cur ? 0 : (h2(F, j + r) - 0.5) * 0.1), (2 - j) * 0.28 + (r - 1) * 0.05, cur ? 0.6 : 0.45, { col: r === 0 ? WHITE : r === 1 ? CYAN : PINK, add: r ? 1 : 0, a: cur ? 1 : 0.5, rough: cur ? 0 : 0.15 }));
        if (cur && k < 0.02) { block = 0.5; inv = 1; }
      }
      top(() => text('It', SERIF, -AS * 0.75, 0.6, 0.3, { col: WHITE }));
    }
    const pz = L ? punch(t - (L.w.filter(x => x <= t).pop() || L.at), 0.25) : 0;
    return {
      bg: dark ? { mode: 0, a: INK, b: INK, c: INK } : { mode: 6, a: INK, b: PINK, c: WHITE, p: [0.04 * Math.sin(t * 3), 0, 0, 0] },
      cam: { z: G.D - pz * 0.35, roll: (h2(F >> 1, 4) - 0.5) * 0.03, x: (h2(F >> 1, 5) - 0.5) * 0.03 },
      post: { dot: dark ? 0 : 0.35, block, inv, slice: block * 0.7, rgb: block * 0.01 + pz * 0.004, scan: 0.15, zb: pz * 0.08 }, bloom: { s: dark ? 0.6 : 0.1, th: 0.95 }
    };
  };

  // 75.7–78.5: モヤモヤし! then a post
  SC.gram = function (t, lt) {
    const L = lineAt(t);
    if (t < 76.35) {
      const k = t - 75.78;
      top(() => text('モヤモヤし!', SERIF, 0, 0, fit('モヤモヤし!', SERIF, AS * 1.6), {
        col: [0.95, 0.8, 1], rough: 0.07, a: clamp(k * 8, 0, 1),
        each: (i) => ({ dx: Math.sin(t * 5 + i) * 0.05, dy: Math.cos(t * 4 + i * 1.3) * 0.08, s: 1 + Math.sin(t * 3 + i) * 0.06 })
      }));
      return { bg: { mode: 3, a: [0.6, 0.55, 0.75], b: [0.35, 0.3, 0.5], c: VIO, p: [0.8, 0, 0, 0] }, cam: { z: G.D }, trail: { decay: 0.7, flow: 0.015 }, post: { rgb: 0.004 }, bloom: { s: 0.35, th: 0.9 } };
    }
    const k = t - 76.35, up = 1 - punch(k, 0.2);
    const cy = -1.6 * (1 - up) + 0.02;
    rect(0, cy, 0.62, 0.86, 0.02, WHITE, 1, { f0: 0.04 });
    rect(0, cy + 0.12, 0.56, 0.56, 0.02, PINK, 1);
    shape(8, 0, cy + 0.12, 0.56, 0.56, 0.02, YEL, 1, { f0: 0.035, f1: 0.1, f3: 0.35 });
    text('ME', SERIF, 0, cy + 0.12, 0.62, { col: WHITE, rot: 0.02, glow: 0.2 });
    shape(2, -0.5, cy + 0.76, 0.04, 0.04, 0, PINK, 1);
    text(H.text('feed.handle'), GOTH, -0.43, cy + 0.76, 0.05, { ax: 0, col: INK, rot: 0.02 });
    if (is(L, 'モヤモヤ')) {
      const P_ = row(L.words, [1, 2, 3, 4, 5], GOTH, 0.085, -0.56, cy - 0.56, 0, { col: INK });
      slam(L, t, (j) => j ? P_[j] : null, { pop: 0.3, ghost: false, drop: 0.1 });
      const hk = t - 77.3;
      if (hk > 0) {
        const s = 1 + 1.2 * punch(hk, 0.12);
        shape(5, -0.5, cy - 0.72, 0.045 * s, 0.045 * s, 0, RED, 1);
        text(H.text('feed.likes'), GOTH, -0.42, cy - 0.72, 0.05, { ax: 0, col: INK });
        rect(AS * 0.5, 0.55, 0.36 * s, 0.11 * s, -0.2, PINK, 1, { line: 0.014 });
        text(H.text('feed.posted'), GOTH, AS * 0.5, 0.55, 0.15 * s, { col: PINK, rot: -0.2 });
      }
    }
    bursts(t, { cols: [PINK, WHITE, CYAN], n: 8 });
    return { bg: { mode: 12, a: PINK, b: CYAN, c: VIO }, cam: { z: G.D, roll: -0.02 }, post: { rgb: 0.002 }, bloom: { s: 0.6 } };
  };

  // 78.5–84.9: the Sphere spells our initials
  SC.sphere = function (t, lt) {
    const L = lineAt(t);
    // the couple's initials: U and each of them, one after another
    const M = H.text('members') || [];
    if (M.length && t > 79.9) {
      const mi = Math.min(M.length - 1, Math.floor((t - 79.9) / (5.0 / M.length)));
      sphereWord = 'U ♥ ' + M[mi].en;
      ticker(M.map(m => m.ko + ' ' + m.en).join('  ♥  ') + '  ♥  ', -0.83, 0.07, 0.35, INK, CORAL, t, { rot: 0.02, a: 0.95 });
    }
    const R = 0.34 + 0.12 * smooth(78.5, 82, t);
    if (is(L, 'Sphere')) {
      slam(L, t, (j) => [
        { x: 0, y: 0.8, size: 0.26, fi: SERIF, col: WHITE, line: 0, track: 0.25, glow: 0.1 },
        { x: -AS * 0.78, y: 0.25, size: 0.26, fi: SERIF, col: CYAN, vert: true, glow: 0.1 },
        ...[2, 3].map(j => row(L.words, [2, 3], GOTH, 0.17, 0, -0.66, 0.5, { col: WHITE })[j]),
        { x: AS * 0.62, y: -0.1, size: 0.3, fi: GOTH, col: YEL, glow: 0.15, rot: Math.PI / 2 }][j]);
    }
    if (t > 81.6) {
      for (let f = 0; f < 5; f++) {
        const t0 = 81.7 + f * 0.6, k = t - t0;
        if (k < 0 || k > 1.4) continue;
        const ox = (hash(f * 3) - 0.5) * AS * 1.6, oy = 0.2 + hash(f * 5) * 0.6;
        for (let q = 0; q < 24; q++) {
          const a = q / 24 * 6.283, r = k * 0.7;
          shape(3, ox + Math.cos(a) * r, oy + Math.sin(a) * r - k * k * 0.2, 0.025 * (1 - k / 1.4), 0.025 * (1 - k / 1.4), 0, [PINK, CYAN, YEL][f % 3], 1, { add: 1, glow: 1 });
        }
      }
    }
    return {
      bg: { mode: 7, a: PINK, b: YEL, c: [1, 0.35, 0.7], p: [R, 0.08, t < 79.9 ? (79.9 - t) * 0.5 : 0, 0], kick: K.env },
      cam: { z: G.D - lt * 0.05, roll: Math.sin(t * 0.5) * 0.02 }, post: { rgb: 0.002 }, bloom: { s: 0.7, th: 0.75 }
    };
  };

  // 84.9–91.6: posters, the school paper, a spinning headline
  SC.news = function (t, lt) {
    const L = lineAt(t);
    const P = H.text('news.posters');
    const nK = count(KICK, t) - count(KICK, 84.9);
    for (let i = Math.max(0, nK - 22); i < nK; i++) {
      const x = (hash(i * 3.3) - 0.5) * AS * 1.9, y = (hash(i * 5.1) - 0.5) * 1.7, r = (hash(i * 7.7) - 0.5) * 0.4;
      const k = t - KICK[count(KICK, 84.9) + i];
      const s = 1 + 0.6 * punch(k, 0.1);
      const c = [WHITE, YEL, [0.95, 0.9, 0.82], RED][i % 4];
      rect(x, y, 0.22 * s, 0.3 * s, r, c, 1);
      text(P[i % P.length], GOTH, x, y, 0.2 * s, { col: i % 4 === 3 ? WHITE : INK, rot: r, sx: 0.85 });
    }
    if (is(L, '上げろ')) {
      const head = t > 88.2;
      if (!head) {
        slam(L, t, (j) => [
          { x: -AS * 0.55, y: 0.25, size: 0.7, fi: SERIF, col: INK, vert: true, shadow: [0.025, -0.025], shadowCol: RED },
          { x: AS * 0.1, y: 0.5, size: 0.3, fi: SERIF, col: INK, shadow: [0.02, -0.02], shadowCol: WHITE },
          { x: AS * 0.1, y: 0.05, size: 0.5, fi: GOTH, col: RED, shadow: [0.02, -0.02], shadowCol: INK },
          { x: AS * 0.1, y: -0.5, size: 0.42, fi: SERIF, col: INK, shadow: [0.02, -0.02], shadowCol: YEL }, null][j]);
      }
    }
    // the paper spins in: a headline
    if (t > 88.2) {
      const e = 1 - punch(t - 88.2, 0.6);
      const spin = (1 - e) * 14, s = lerp(0.05, 1, e);
      const shake = smooth(90.4, 91.7, t);
      const r = spin + (h2(F, 3) - 0.5) * 0.08 * shake;
      const pw = 1.25 * s, ph = 0.8 * s;
      rect(0, 0, pw, ph, r, [0.95, 0.93, 0.88], 1);
      const R2 = (x, y) => [x * Math.cos(r) - y * Math.sin(r), x * Math.sin(r) + y * Math.cos(r)];
      let p = R2(0, 0.66 * s); text(H.text('news.paper'), SERIF, p[0], p[1], 0.1 * s, { col: INK, rot: r, track: 0.1 });
      p = R2(0, 0.57 * s); rect(p[0], p[1], 1.15 * s, 0.006 * s, r, INK, 1);
      p = R2(0, 0.3 * s); text(H.text('news.head'), GOTH, p[0], p[1], fit(H.text('news.head'), GOTH, 2.2) * s, { col: RED, rot: r });
      p = R2(0, 0.03 * s); text('headline', SERIF, p[0], p[1], 0.14 * s, { col: INK, rot: r });
      for (let c = 0; c < 4; c++) for (let l = 0; l < 7; l++) {
        p = R2((c - 1.5) * 0.58 * s, (-0.15 - l * 0.08) * s);
        rect(p[0], p[1], (0.24 - (l === 6 ? 0.1 : 0)) * s, 0.012 * s, r, [0.45, 0.42, 0.45], 1);
      }
      p = R2(0.75 * s, -0.42 * s); shape(5, p[0], p[1], 0.12 * s, 0.12 * s, r, RED, 1);
    }
    const build = smooth(90.4, 91.7, t);
    return {
      bg: { mode: 8, a: RED, b: INK, c: INK },
      cam: { z: G.D - build * 0.6, roll: build * (h2(F, 7) - 0.5) * 0.06 },
      post: { rgb: 0.002 + build * 0.01, slice: build * 0.3, flash: t > 91.6 ? 0.7 : 0, dot: 0.15 }, bloom: { s: 0.2, th: 0.95 }
    };
  };

  // 113.8–128.6: the dance break. A river of letters becomes a star; then everything, faster and faster
  const RIVER = [...new Set(LINES.map(l => l.line).join('').replace(/[\s,!?’.]/g, ''))];
  function starPt(u) {
    // a point along a five-point star's outline (u in 0..1)
    const k = u * 10, i = Math.floor(k) % 10, f = k - Math.floor(k);
    const r = i => (i % 2 ? 0.42 : 1), a = i => -Math.PI / 2 + i * Math.PI / 5;
    const x0 = Math.cos(a(i)) * r(i), y0 = -Math.sin(a(i)) * r(i), x1 = Math.cos(a(i + 1)) * r(i + 1), y1 = -Math.sin(a(i + 1)) * r(i + 1);
    return [lerp(x0, x1, f), lerp(y0, y1, f)];
  }
  SC.star = function (t, lt) {
    const form = smooth(114.5, 118.2, t);
    const spin = t > 118.2 ? Math.sin((t - 118.2) * 0.9) * 0.9 : 0;
    const N = 380;
    const Rr = 0.82 * (1 + 0.06 * K.env);
    for (let i = 0; i < N; i++) {
      const ch = RIVER[i % RIVER.length];
      const g = A.get(i % 3 ? GOTH : SERIF, ch); if (!g) continue;
      // in the river: flowing streams
      const lane = i % 5, ph = (t * (0.18 + lane * 0.03) + hash(i)) % 1;
      const rx = (ph - 0.5) * AS * 2.6, ry = Math.sin(ph * 6 + lane * 1.3 + t * 0.6) * 0.35 + (lane - 2) * 0.3;
      // on the star
      const sp = starPt((i / N + t * 0.02) % 1);
      const ca = Math.cos(spin), sa = Math.sin(spin);
      const sx = sp[0] * Rr * ca, sz = sp[0] * Rr * sa, sy = sp[1] * Rr;
      const e = smooth(0, 1, form * 1.4 - hash(i * 3) * 0.4);
      const x = lerp(rx, sx, e), y = lerp(ry, sy, e), z = lerp(-0.5 + hash(i) * -1.5, sz, e);
      const s = lerp(0.09 + hash(i * 7) * 0.09, 0.1, e);
      const col = [PINK, CYAN, YEL, WHITE][i % 4];
      const hx = s * A.cellEm / 2;
      G.quad(x, y, z, hx * Math.cos(spin * e), 0, -hx * Math.sin(spin * e), 0, hx, 0, g, col[0], col[1], col[2], lerp(0.55, 0.95, e), 0, 0, 0.15, 0.5, 0, 0, 0, 0);
    }
    if (form > 0.9) shape(4, 0, 0, 0.22 * (1 + 0.15 * K.env), 0.22 * (1 + 0.15 * K.env), 0, YEL, (form - 0.9) * 10 * 0.8, { glow: 0.6, add: 1 });
    sparkles(t, 50, { cols: [WHITE, PINK, CYAN], size: 0.03 });
    return {
      bg: { mode: 0, a: [0.07, 0.01, 0.12], b: INK, c: INK },
      cam: { z: G.D + 0.4 - form * 0.4, roll: Math.sin(t * 0.4) * 0.05 },
      trail: { decay: 0.8, flow: 0.004, zoom: -0.004, spin: 0.004 },
      post: { rgb: 0.002 + K.env * 0.006 }, bloom: { s: 0.6, th: 0.8 }
    };
  };
  // the montage: every motif, one word each, cut faster and faster
  const SNAPS = [
    { w: 'BIAS', bg: { mode: 1, a: PINK, b: HOT, c: YEL, p: [14, 0.1, 0, 0] }, col: WHITE, fi: GOTH },
    { w: '推し', bg: { mode: 2, a: PINK, b: CYAN, c: VIO, p: [0, 1.4, 0, 1] }, col: PINK, fi: SERIF },
    { w: 'IDOL', bg: { mode: 9, a: PINK, b: INK, c: VIO, p: [3, 0, 0, 0] }, col: YEL, fi: GOTH },
    { w: '宝石箱', bg: { mode: 4, a: [0.55, 0.12, 0.45], b: [0.15, 0.25, 0.6], c: WHITE }, col: WHITE, fi: SERIF },
    { w: 'ME', bg: { mode: 5, a: PINK, b: WHITE, c: WHITE, p: [0.6, 0, 0, 0] }, col: INK, fi: SERIF },
    { w: 'キミ', bg: { mode: 6, a: INK, b: PINK, c: WHITE, p: [0, 0, 0, 0] }, col: INK, fi: GOTH },
    { w: '♥', bg: { mode: 7, a: PINK, b: YEL, c: [1, 0.35, 0.7], p: [0.45, 0.05, 0, 0] }, col: WHITE, fi: GOTH },
    { w: '初恋', bg: { mode: 10, a: PINK, b: HOT, c: YEL, p: [0.55, -0.8, 0, 0] }, col: WHITE, fi: SERIF },
    { w: 'fave', bg: { mode: 3, a: PINK, b: TEAL, c: RED, p: [1, 0, 0, 0] }, col: WHITE, fi: SERIF },
    { w: 'BIAS', bg: { mode: 0, a: INK, b: INK, c: INK }, col: PINK, fi: GOTH },
    { logo: true, bg: { mode: 5, a: WHITE, b: WHITE, c: WHITE, p: [3, 0, 0, 0] }, col: INK, fi: GOTH }
  ];
  SC.montage = function (t) {
    const b = beatPhase(t), b0 = beatPhase(121.9);
    const rel = b - b0;
    // two beats a cut, then one, then half, then a quarter
    let cut;
    if (rel < 8) cut = Math.floor(rel / 2);
    else if (rel < 16) cut = 4 + Math.floor(rel - 8);
    else if (rel < 20) cut = 12 + Math.floor((rel - 16) * 2);
    else cut = 20 + Math.floor((rel - 20) * 4);
    const sn = cut % 5 === 4 ? SNAPS[SNAPS.length - 1] : SNAPS[(cut * 3) % (SNAPS.length - 1)];
    const ph = rel < 8 ? (rel / 2) % 1 : rel < 16 ? rel % 1 : rel < 20 ? (rel * 2) % 1 : (rel * 4) % 1;
    const rush = smooth(128.6, 130.05, t);
    if (sn.logo) {
      logo(0, 0, AS * 1.2 * (1 + 0.15 * punch(ph, 0.3)), INK, 1, { each: i => ({ dx: (h2(i, F) - 0.5) * 0.06 * punch(ph, 0.3) }) });
      return { bg: Object.assign({}, sn.bg), cam: { z: G.D - ph * 0.15, roll: (hash(cut * 7) - 0.5) * 0.1 }, post: { rgb: 0.006, block: ph < 0.12 ? 0.6 : 0, slice: ph < 0.2 ? 0.5 : 0 }, bloom: { s: 0.1, th: 0.98 } };
    }
    const sz = fit(sn.w, sn.fi, AS * (sn.w.length < 3 ? 0.9 : 1.6)) * (1 + 0.25 * punch(ph, 0.3));
    top(() => {
      text(sn.w, sn.fi, (hash(cut) - 0.5) * 0.3 + 0.03, -0.03, sz, { col: sn.col === INK ? PINK : INK, rot: (hash(cut * 3) - 0.5) * 0.3, a: 0.9 });
      text(sn.w, sn.fi, (hash(cut) - 0.5) * 0.3, 0, sz, { col: sn.col, rot: (hash(cut * 3) - 0.5) * 0.3 });
    });
    if (cut % 2) wall([sn.w], t, { rows: 7, size: 0.22, z: -1.5, col: sn.col, a: 0.4, speed: 2, line: 0.07, fi: sn.fi });
    const black = t > 129.95;
    return {
      bg: black ? { mode: 0, a: [0, 0, 0], b: INK, c: INK } : Object.assign({ kick: K.env, beat: BP }, sn.bg, { p: sn.bg.p || [0, 0, 0, 0] }),
      cam: { z: G.D - rush * 1.2 - ph * 0.15, roll: (hash(cut * 7) - 0.5) * 0.15 },
      post: { rgb: 0.004 + rush * 0.015, slice: ph < 0.2 ? 0.5 : rush * 0.4, block: ph < 0.12 ? 0.6 : 0, flash: rush * (BP * 4 % 1 < 0.3 ? 0.5 : 0), inv: black ? 0 : 0, dot: sn.bg.mode === 1 ? 0.2 : 0 },
      bloom: { s: [1, 5].includes(sn.bg.mode) ? 0.2 : 0.5, th: [1, 5, 6].includes(sn.bg.mode) ? 0.95 : 0.85 }
    };
  };

  // 156–162: everything gathers into one star, and goes
  SC.end = function (t, lt) {
    const k = smooth(155.6, 157.4, t);
    const N = 300;
    for (let i = 0; i < N; i++) {
      const ch = RIVER[i % RIVER.length];
      const g = A.get(i % 2, ch); if (!g) continue;
      const a = hash(i) * 6.283, r = (0.3 + hash(i * 3) * 1.6) * (1 - k);
      const x = Math.cos(a + k * 3) * r * AS, y = Math.sin(a + k * 3) * r;
      const s = 0.12 * (1 - k * 0.9);
      const col = [PINK, CYAN, YEL, WHITE][i % 4];
      const hx = s * A.cellEm / 2;
      G.quad(x, y, 0, hx, 0, 0, 0, hx, 0, g, col[0], col[1], col[2], 1 - smooth(157.2, 157.6, t), 0, 0, 0.5, 1, 0, 0, 0, 0);
    }
    const gl = smooth(157.1, 157.5, t) * (1 - smooth(158.2, 161.5, t));
    if (gl > 0) {
      const s = 0.12 + 0.6 * Math.exp(-(t - 157.4) * 3);
      shape(3, 0, 0, s * gl, s * gl, t * 0.3, WHITE, 1, { glow: 1, add: 1 });
      shape(9, 0, 0, s * 4, s * 4, 0, PINK, 0.3 * gl, { add: 1 });
    }
    if (t > 157.9) {
      // the last shot: the logo, huge, on white; then dark
      const w0 = smooth(157.9, 158.0, t), out = smooth(160.4, 161.9, t);
      const kk = t - 157.9;
      logo(0, 0.05, AS * 1.25 * (1 + 0.35 * punch(kk, 0.15)), INK, 1, { each: i => ({ dx: (h2(i, F >> 1) - 0.5) * 0.05 * punch(kk, 0.4) }) });
      text(H.text('title').toUpperCase(), GOTH, 0, -0.55, 0.11, { col: INK, track: 0.4, a: smooth(158.6, 159.2, t) });
      return { bg: { mode: 5, a: WHITE, b: WHITE, c: WHITE, p: [3, 0, 0, 0], amt: 1 - out }, cam: { z: G.D + kk * 0.08 }, post: { scan: 0.15, grain: 0.06, vig: 0.15, tint: [1 - out, 1 - out, 1 - out], block: Math.exp(-kk * 8) * 0.6 }, bloom: { s: 0.1, th: 0.98 } };
    }
    return { bg: { mode: 0, a: sc(INK, 1 - k * 0.6), b: INK, c: INK }, cam: { z: G.D }, trail: { decay: 0.8 }, post: { scan: 0.3 }, bloom: { s: 1.2, th: 0.5 } };
  };

  // ---- from the music video: LED walls of words, stacked capitals, IT'S ME in the dark, a lantern street
  // a glyph on a plane: ax, ay are the plane's directions (unit), s the size
  function glyph3(g, x, y, z, ax, ay, s, col, a, glow, add) {
    const h = s * A.cellEm / 2;
    G.quad(x, y, z, ax[0] * h, ax[1] * h, ax[2] * h, ay[0] * h, ay[1] * h, ay[2] * h, g, col[0], col[1], col[2], a, 0, 0, glow || 0, add || 0, 0, 0, 0.02, 0);
  }
  // the corridor: both walls lit with words, flying in
  function corridor(t, o) {
    const words = (H.text('walls') || ["IT'S ME"]).map(w => w + '  ');
    const speed = o.speed || 3, len = 34, rows = 6, sz = 0.3, wx = o.w || 1.55, hy = 1.0;
    const camZ = G.D;
    // ceiling light strip
    for (let i = 0; i < 14; i++) {
      const z = camZ - 2 - ((i * 2.4 - t * speed) % (len) + len) % len;
      shape(1, 0, hy * 1.02, 0.2, 0.012, 0, [0.75, 0.95, 1], clamp(1 + (z - camZ) / len, 0, 1), { z, glow: 0.6, add: 1 });
    }
    for (const side of [-1, 1]) {
      for (let r = 0; r < rows; r++) {
        const word = words[(r + (side > 0 ? 1 : 0)) % words.length];
        const lay = hlay(word, GOTH);
        const per = lay.w * sz;
        const y = -hy + (r + 0.5) * (2 * hy / rows);
        const off = ((t * speed + r * 0.7) % per + per) % per;
        // from far to near
        for (let k = Math.ceil(len / per); k >= -1; k--) {
          for (let ci = lay.chars.length - 1; ci >= 0; ci--) {
            const ch = lay.chars[ci];
            const along = k * per + ch.x * sz - off;           // distance in front of the camera
            const z = camZ - 0.6 - along * 1;
            if (along < -0.4 || along > len) continue;
            const fog = clamp(1 - along / len, 0, 1);
            // tiles behind the letters, then the letter (text runs away from us along each wall)
            const dir = side < 0 ? [0, 0, -1] : [0, 0, 1];
            const cz = z - side * 0 ;
            const col = mixc([0.25, 0.05, 0.05], o.col || CORAL, fog * (0.6 + 0.4 * ((r + k) % 2)));
            G.quad(side * wx, y, cz - dir[2] * (ch.adv * sz * 0.5) * side * 0, 0, 0, -sz * 0.5 * ch.adv, 0, sz * 0.48, 0, null, col[0] * 0.35, col[1] * 0.35, col[2] * 0.35, fog, 1, 0, 0, 0, 0, 0, 0, 0);
            glyph3(ch.g, side * (wx - 0.01), y, z + (side > 0 ? 0 : 0), side < 0 ? [0, 0, -1] : [0, 0, 1], [0, 1, 0], sz * 0.95, mixc(col, BONE, 0.25 * fog), fog, 0.25, 0.4);
          }
        }
      }
    }
    // the floor: dark, with a reflection of the light
    shape(10, 0, -hy, 1.6, 6, 0, (o.col || CORAL), 0.25, { z: camZ - 7, f0: -1 });
  }
  // a round room walled with words, turning around us
  function room(t, o) {
    const words = (H.text('walls') || ["IT'S ME"]).map(w => w + '  ');
    const Rr = 3.2, rows = 7, sz = 0.36, cz = G.D;
    for (let r = 0; r < rows; r++) {
      const word = words[r % words.length];
      const lay = hlay(word, GOTH);
      const y = (r - (rows - 1) / 2) * 0.42;
      const circ = 2 * Math.PI * Rr, per = lay.w * sz;
      const nRep = Math.floor(circ / per);
      const spin = t * (o.spin || 0.25) * (r % 2 ? -1 : 1);
      for (let k = 0; k < nRep; k++) for (const ch of lay.chars) {
        const a = ((k * per + ch.x * sz) / circ) * 2 * Math.PI * (circ / (nRep * per)) + spin;
        const s = Math.sin(a), c = Math.cos(a);
        if (c < 0.05) continue;                         // only the half in front of us
        const x = s * Rr, z = cz - c * Rr;
        glyph3(ch.g, x, y, z, [c, 0, s], [0, 1, 0], sz, mixc([0.3, 0.06, 0.06], o.col || CORAL, 0.4 + 0.6 * c), 0.95, 0.2, 0.3);
      }
    }
  }
  // stacked capitals: the chorus lines pile up, full width, in white
  function capsStack(t, o) { top(() => capsStack_(t, o)); }
  function capsStack_(t, o) {
    o = o || {};
    const cur = LINES.filter(L => L.at - 0.02 <= t && (isQ(L) || isA(L)) && L.at > (o.from || 0));
    const keep = cur.slice(-(o.n || 4));
    const lead = o.lead || 0.36, size = o.size || 0.34;
    keep.forEach((L, r) => {
      const y = (o.y == null ? 0.7 : o.y) - r * lead;
      const words = L.words.map(w => w.toUpperCase());
      let x = -AS * 0.94;
      words.forEach((w, j) => {
        const k = t - L.w[j];
        if (k < 0) return;
        const swap = hash(F * 3 + r * 7 + j) < 0.012;
        text(swap ? w.split('').reverse().join('') : w, GOTH, x, y, size, { ax: 0, col: o.col || BONE, a: k < 0.05 ? 0.6 : 1, sx: 1.05 });
        if (k < 0.15) text(w, GOTH, x + 0.03, y, size, { ax: 0, col: CORAL, add: 1, a: 0.8 });
        x += (widthOf(w, GOTH) * 1.05 + 0.26) * size;
      });
    });
  }
  // the music video's ERR in the corner
  function hud(col) {
    text(H.text('hud.err'), GOTH, -AS * 0.92, -0.78, 0.07, { ax: 0, col: col || CORAL, a: F % 20 < 14 ? 0.9 : 0.3 });
    logo(AS * 0.86, 0.85, 0.2, BONE, 0.85);
  }
  SC.corridor = function (t, lt, o) {
    o = o || {};
    const L = lineAt(t);
    const build = o.build ? smooth(37.4, 40.5, t) : 0;
    corridor(t, { speed: 3 + build * 14 + (o.fast || 0) });
    if (is(L, 'なぜ')) {
      const P_ = row(L.words, [0, 1, 2], SERIF, 0.4, 0, 0.05, 0.5, { col: BONE });
      P_[1].col = CORAL;
      slam(L, t, (j) => P_[j], { drop: 0.3 });
    }
    if (o.caps) capsStack(t, { from: o.from, n: 4 });
    else if (L && /It’s Me/.test(L.line)) top(() => text("IT'S ME", GOTH, 0, 0, fit("IT'S ME", GOTH, AS * 1.2), { col: BONE }));
    if (build > 0) {
      const strobe = build > 0.5 && (BP % 1) < 0.12 ? 1 : 0;
      text("IT'S ME", GOTH, 0, 0, 0.3 + build * 1.6, { col: BONE, a: build * (0.25 + 0.75 * strobe) });
    }
    hud();
    return {
      bg: { mode: 0, a: TEAL0, b: INK, c: INK },
      cam: { z: G.D, roll: Math.sin(t * 0.6) * 0.03 + build * Math.sin(t * 9) * 0.03 },
      post: { duo: 0.75, rgb: 0.003 + build * 0.012 + K.env * 0.004, slice: build * 0.35, flash: t > 40.42 && o.build ? smooth(40.42, 40.5, t) : 0, grain: 0.09, scan: 0.35 },
      trail: { decay: 0.55 + build * 0.2, zoom: 0.01 + build * 0.03 },
      bloom: { s: 0.45, th: 0.72 }
    };
  };
  SC.room = function (t, lt, o) {
    o = o || {};
    room(t, { spin: 0.3 });
    capsStack(t, { from: o.from, n: 4 });
    const L = lineAt(t);
    if (L && /It’s Me/.test(L.line)) top(() => text("IT'S ME", GOTH, 0, -0.1, fit("IT'S ME", GOTH, AS * 1.1), { col: BONE }));
    hud();
    return { bg: { mode: 0, a: TEAL0, b: INK, c: INK }, cam: { z: G.D, roll: Math.sin(t * 0.4) * 0.04 }, post: { duo: 0.75, rgb: 0.003 + K.env * 0.006, grain: 0.09, scan: 0.35 }, bloom: { s: 0.6, th: 0.6 } };
  };
  // IT'S ME in the dark, the frame breaking up around it
  SC.dark = function (t, lt) {
    const L = lineAt(t) || LINES.filter(x => x.at <= t && /It’s Me/.test(x.line)).pop();
    const rows = 7;
    for (let r = 0; r < rows; r++) {
      const inv = hash(r * 5 + (F >> 2)) < 0.25;
      const y = (r - 3) * 0.3;
      if (inv) rect(0, y, AS * 1.2, 0.14, 0, BONE, 0.95);
      const word = "IT'S ME IT'S ME IT'S ME IT'S ME IT'S ME ";
      const off = ((t * (r % 2 ? 0.4 : -0.4)) % 1.6);
      text(word, GOTH, -AS * 1.3 + off, y, 0.27, { ax: 0, col: inv ? INK : (r % 3 ? [0.4, 0.42, 0.42] : CORAL), line: inv ? 0 : (r % 2 ? 0.08 : 0), a: inv ? 1 : 0.6 });
    }
    const k = L ? t - L.at : 1;
    top(() => {
      rect(0, 0, AS * 0.62, 0.3, 0, INK, 0.92);
      text("IT'S ME", GOTH, 0, 0, 0.42 * (1 + 0.3 * punch(k, 0.12)), { col: BONE, track: 0.02 });
    });
    for (let i = 0; i < 6; i++) {
      if (hash(i * 13 + (F >> 1)) > 0.5) continue;
      rect((h2(i, F >> 1) - 0.5) * AS * 2, (h2(i + 7, F >> 1) - 0.5) * 1.8, 0.08 + hash(i + F) * 0.3, 0.03 + hash(i * 3 + F) * 0.12, 0, [0.4, 0.42, 0.43], 0.85);
    }
    hud();
    return { bg: { mode: 0, a: [0.02, 0.025, 0.03], b: INK, c: INK }, cam: { z: G.D - k * 0.05 }, post: { duo: 0.6, rgb: 0.006 + K.env * 0.01, block: 0.35, slice: 0.2, grain: 0.1, scan: 0.4 }, bloom: { s: 0.4, th: 0.8 } };
  };
  // a street of red lanterns, the words stacked in front
  SC.lanterns = function (t, lt, o) {
    o = o || {};
    for (let i = 13; i >= 0; i--) {
      for (const side of [-1, 1]) {
        const z = -((i * 1.1 - t * 0.8) % 15.4 + 15.4) % 15.4 + 1.2;
        const fog = clamp(1 + (z - 1) / 15, 0, 1);
        const x = side * 1.05, y = -0.05 + (i % 2) * 0.18 + Math.sin(t * 1.3 + i) * 0.02;
        shape(9, x, y, 0.45, 0.45, 0, CORAL, 0.35 * fog, { z, add: 1 });
        shape(2, x, y, 0.13, 0.16, 0, mixc([0.5, 0.05, 0.05], [1, 0.32, 0.25], fog), fog, { z, glow: 0.8 });
        shape(1, x, y, 0.135, 0.008, 0, [0.2, 0.05, 0.03], fog, { z });
        shape(1, x, y + 0.6, 0.003, 0.44, 0, [0.15, 0.15, 0.15], fog, { z });
      }
    }
    capsStack(t, { from: o.from, n: 4 });
    hud();
    return { bg: { mode: 12, a: [0.5, 0.1, 0.08], b: [0.1, 0.25, 0.25], c: TEAL0 }, cam: { z: G.D, roll: Math.sin(t * 0.5) * 0.02 }, post: { duo: 0.55, rgb: 0.003 + K.env * 0.005, grain: 0.09, scan: 0.3 }, bloom: { s: 0.8, th: 0.55 } };
  };

  // ---- the running order
  const P = (fn, o) => (t, lt) => SC[fn](t, lt, o);
  const STORY = [
    { at: 0, f: P('pre') },
    { at: 1.8, f: P('chant') },
    { at: 6.9, f: P('title') },
    { at: 14.6, f: P('idol') },
    { at: 20.9, f: P('feed') },
    { at: 24.3, f: P('jewel') },
    { at: 27.5, f: P('liquid') },
    { at: 35.4, box: 1, f: P('corridor', { build: true }) },
    { at: 40.5, f: P('pop', { mode: 1 }) },
    { at: 46.8, f: P('pop', { mode: 9, shift: 2 }) },
    { at: 52.35, f: P('white') },
    { at: 55.5, box: 1, f: P('mirror', {}) },
    { at: 63.05, f: P('fave') },
    { at: 66.0, f: P('speed') },
    { at: 75.7, f: P('gram') },
    { at: 78.5, f: P('sphere') },
    { at: 84.9, f: P('news') },
    { at: 91.65, f: P('pop', { mode: 2, screens: true, shift: 1 }) },
    { at: 97.95, box: 1, f: P('corridor', { caps: true, from: 97.9, fast: 2 }) },
    { at: 103.6, box: 1, f: P('dark') },
    { at: 106.5, box: 1, f: P('mirror', { inv: true, shards: true, speed: 1.1, spin: 0.15 }) },
    { at: 113.8, box: 1, f: P('star') },
    { at: 121.9, f: P('montage') },
    { at: 130.05, f: P('pop', { mode: 1, shift: 4 }) },
    { at: 133.8, f: P('white') },
    { at: 136.45, f: P('lanterns', { from: 136.4 }) },
    { at: 139.9, f: P('star') },
    { at: 142.05, box: 1, f: P('dark') },
    { at: 144.4, box: 1, f: P('corridor', { caps: true, from: 144.3, fast: 3 }) },
    { at: 148.45, f: P('white') },
    { at: 149.3, f: P('pop', { mode: 2, screens: true, shift: 2 }) },
    { at: 151.9, f: P('room', { from: 151.8 }) },
    { at: 154.75, f: P('white') },
    { at: 155.6, f: P('end') }
  ];
  H.STORY = STORY;
  const CUTS = [...new Set([...STORY.map(s => s.at), ...LINES.map(l => l.at)])].sort((a, b) => a - b);


  // ---- the edit: stutters, impact frames, split screens, letterbox, datamosh
  const at = (re, from) => LINES.filter(L => re.test(L.line) && (from == null || L.at >= from));
  // stutters: the picture loops or freezes while the music runs on
  const STUT = [];
  const loopWords = (L, len, dur) => L.w.forEach((w, j) => { if (j || !/^(Oh|It)$/.test(L.words[0])) STUT.push({ a: w, b: w + dur, len }); });
  at(/^Oh my/).forEach(L => loopWords(L, 2 / 30, 0.2));
  at(/^It can’t/).forEach(L => L.w.slice(1).forEach(w => STUT.push({ a: w, b: w + 0.27, len: 3 / 30 })));
  at(/^Who\? Who\?/).forEach(L => L.w.forEach(w => STUT.push({ a: w, b: w + 0.2, len: 2 / 30 })));
  [[40.3, 40.58], [55.36, 55.62], [91.32, 91.62], [113.5, 113.78]].forEach(([a, b]) => STUT.push({ a, b, len: 0 }));
  function remap(t) {
    for (const s of STUT) if (t >= s.a && t < s.b) return s.len ? s.a + Math.floor(((t - s.a) % s.len) * 30) / 30 : s.a;
    return t;
  }
  // impact frames: every It's Me, the drop's Me, each chorus downbeat, the title, the last logo
  const IMPACT = [...at(/It’s Me|^Me/).map(L => L.at), 40.6, 91.74, 130.1, 10.25, 157.9].sort((a, b) => a - b);
  // split screens on chosen chorus bars: [line start, panels]
  const PANEL = [[48.5, 3], [49.42, 4], [99.64, 3], [100.56, 4], [146.42, 3], [147.02, 4], [152.0, 3], [153.5, 4]]
    .map(([a, n]) => { const L = LINES.find(x => Math.abs(x.at - a) < 0.06); return L ? { a: L.at, b: L.end, n } : null; }).filter(Boolean);

  // the title screen: the stage waits
  function idle(clock) {
    sparkles(clock, 30, { cols: [WHITE, PINK, CYAN], size: 0.035 });
    wall(H.text('wall'), clock, { rows: 9, size: 0.26, z: -2.5, col: VIO, a: 0.4, line: 0.07, speed: 0.3 });
    return { bg: { mode: 2, a: PINK, b: CYAN, c: VIO, p: [0, 0.7, -0.1, 1], beat: clock * 1.2 }, cam: { z: G.D }, post: { scan: 0.3 }, bloom: { s: 0.9 } };
  }

  D.frame = function (tReal, mode, clock) {
    AS = G.aspect || 1.78;
    const t = mode === 'title' ? tReal : remap(tReal);
    T = t; F = Math.floor(t * 30);
    K = hit(KICK, t, 9); S_ = hit(SN, t, 12); BP = beatPhase(t);
    G.begin();
    sphereWord = null;
    let out;
    if (mode === 'title') out = idle(clock);
    else {
      let i = 0; while (i + 1 < STORY.length && STORY[i + 1].at <= t) i++;
      const s = STORY[i];
      out = s.f(t, t - s.at);
      out.box = s.box ? 1 - punch(t - s.at, 0.08) : 0;
    }
    // a small subtitle under everything, the way a music video carries its words
    if (mode !== 'title') {
      const L = lineAt(t);
      if (L && t - L.at < 3) {
        const light = out.bg && (out.bg.mode === 5 || out.bg.mode === 6 || out.bg.mode === 8 || (out.bg.mode === 1 && out.bg.a === YEL));
        top(() => text(L.line, SERIF, 0, out.box ? -0.68 : -0.9, 0.05, { col: light && !out.box ? INK : WHITE, a: 0.85, track: 0.08 }));
      }
    }
    G.sphereTex(sphereWord || H.text('sphere'));
    out.t = t;
    out.post = out.post || {};
    out.cam = out.cam || {};
    // the kick jolts the camera; the cut glitches the frame
    if (mode !== 'title') {
      const j = K.env * ((KAMP[K.i] || 0.5) * 0.06);
      out.cam.x = (out.cam.x || 0) + (hash(K.i * 1.3) - 0.5) * j;
      out.cam.y = (out.cam.y || 0) + (hash(K.i * 2.1) - 0.5) * j;
      out.cam.z = (out.cam.z == null ? G.D : out.cam.z) - K.env * 0.06;
      const ci = count(CUTS, t) - 1;
      const age = ci >= 0 ? t - CUTS[ci] : 9;
      const g = age < 0.1 ? 1 - age / 0.1 : 0;
      const p = out.post;
      p.rgb = (p.rgb || 0) + g * 0.012 + S_.env * 0.002;
      p.slice = Math.max(p.slice || 0, g * 0.8, S_.env * 0.12);
      p.block = Math.max(p.block || 0, g * 0.5);
      p.seed = Math.floor(tReal * 30) % 997;
      // the edit, on the real clock
      const fr = k => Math.floor((tReal - k) * 30);
      const ii = count(IMPACT, tReal) - 1;
      if (ii >= 0 && fr(IMPACT[ii]) < 2) p.impact = fr(IMPACT[ii]) + 1;
      const pn = PANEL.find(x => tReal >= x.a && tReal < x.b);
      if (pn) p.panels = pn.n;
      p.box = Math.max(p.box || 0, (out.box || 0) * 0.128);
      const si = count(STORY.map(s => s.at), tReal) - 1;
      const sAge = si > 0 ? tReal - STORY[si].at : 9;
      if (sAge < 0.2) p.mosh = 1 - sAge / 0.2;
      const strong = (KAMP[K.i] || 0) > 0.7 ? punch(K.age, 0.1) : 0;
      p.zb = Math.max(p.zb || 0, strong * 0.06);
      // an impact frame is clean: one flat silhouette, nothing torn
      if (p.impact) { p.block = 0; p.slice = 0; p.mosh = 0; p.rgb = 0.004; p.panels = 0; p.zb = 0.04; p.flash = 0; }
    }
    return out;
  };
  D.chars = function () {
    const T = window.HANA_TEXT;
    let s = JSON.stringify(T) + 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789,.!?’\'·♥★✦:@#';
    s += H.text('title').toUpperCase();
    return s;
  };
})();
